/**
 * useSuggestedQuestions.ts - Hook for fetching widget post suggested questions
 *
 * Fetches the _widget tagged post for a given circle and parses
 * the structured question data from its content (nsHtml) field.
 *
 * Content format (written by admins in the post editor textarea):
 *   Post title field = widget title (no tag needed)
 *   <question>What is water scarcity?</question>
 *   <question>How does desalination work?</question>
 *   <description>Optional intro text</description>
 *   <image>https://example.com/photo.jpg</image>
 *
 * A question may carry an optional retrieval date filter, applied when tapped:
 *   <question date="last_7_days">What is the latest news about water scarcity?</question>
 *   <question from="2024-01-01" to="2024-03-31">Q1 2024 policy review</question>
 * Relative tokens follow last_<N>_<unit> (unit: hour|day|week|month|year, N
 * optional/defaults to 1), e.g. last_24_hours, last_45_days, last_3_months,
 * last_year. See questionDateRange.ts.
 *
 * Image resolution order:
 *   1. <image> custom tag in content body
 *   2. <img> tag inserted by rich-text editor (direct image attachment/embed)
 *   3. post.image field (post cover image / direct attachment)
 *
 * The HTML from the rich-text editor may wrap content in <p> tags, so
 * we strip standard HTML before parsing custom tags.
 */

import useSWR from 'swr';
import { useApi } from 'core';
import { getImageUrl } from '@/utilities/imageUtils';
import { QuestionDateSpec, parseQuestionDateAttrs } from '@/utilities/questionDateRange';

export interface SuggestedQuestion {
    text: string;
    dateSpec?: QuestionDateSpec;
}

interface UseSuggestedQuestionsResult {
    questions: SuggestedQuestion[];
    title?: string;
    description?: string;
    image?: string;
    isLoading: boolean;
}

const EMPTY_RESULT: UseSuggestedQuestionsResult = {
    questions: [],
    isLoading: false,
};

/**
 * Extract image URL from various sources in order of priority.
 */
function extractImageUrl(html: string, cleaned: string, postImage?: any): string | undefined {
    // 1. Custom <image> tag with plain URL
    const imageTagMatch = cleaned.match(/<image>([\s\S]*?)<\/image>/i);
    if (imageTagMatch) {
        const imageContent = imageTagMatch[1].trim();
        // Editor may wrap URL in <a href="...">
        const hrefMatch = imageContent.match(/href="([^"]+)"/i);
        if (hrefMatch) {
            const url = hrefMatch[1].trim();
            if (url.startsWith('http')) return url;
        }
        const plainUrl = imageContent.replace(/<[^>]*>/g, '').trim();
        if (plainUrl.startsWith('http')) return plainUrl;
    }

    // 2. <img> tag embedded by the rich-text editor (in original HTML before cleaning)
    const imgTagMatch = html.match(/<img[^>]+src="([^"]+)"/i);
    if (imgTagMatch) {
        const src = imgTagMatch[1].trim();
        if (src.startsWith('http')) return src;
    }

    // 3. post.image field (direct attachment with uuid)
    if (postImage?.uuid) {
        const url = getImageUrl(postImage, { width: 400, height: 300, fit: 'crop', auto: 'format' });
        if (url) return url;
    }

    return undefined;
}

/**
 * Parse widget content from the post's nsHtml field.
 * Supports custom XML-like tags inside HTML content.
 */
function parseWidgetContent(html: string, postTitle?: string, postImage?: any): UseSuggestedQuestionsResult | null {
    if (!html || typeof html !== 'string') return null;

    try {
        // Strip standard HTML tags but keep our custom tags and <img> is handled separately
        const cleaned = html
            .replace(/<\/?(p|br|span|div|strong|em|b|i|ul|ol|li|h[1-6])[^>]*\/?>/gi, '\n')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");

        // Extract questions: <question [date=".."|from=".." to=".."]>...</question>
        const questionRegex = /<question\b([^>]*)>([\s\S]*?)<\/question>/gi;
        const questions: SuggestedQuestion[] = [];
        let match: RegExpExecArray | null;
        while ((match = questionRegex.exec(cleaned)) !== null) {
            const text = match[2].trim();
            if (text.length > 0) {
                questions.push({ text, dateSpec: parseQuestionDateAttrs(match[1]) });
            }
        }

        if (questions.length === 0) return null;

        // Title: use post.title field, fall back to <titleTag> in content for backwards compat
        const titleTagMatch = cleaned.match(/<titleTag>([\s\S]*?)<\/titleTag>/i);
        const title = postTitle?.trim() || (titleTagMatch ? titleTagMatch[1].trim() : undefined);

        // Extract optional description: <description>...</description>
        const descMatch = cleaned.match(/<description>([\s\S]*?)<\/description>/i);
        const description = descMatch ? descMatch[1].trim() : undefined;

        // Extract image from multiple sources
        const image = extractImageUrl(html, cleaned, postImage);

        return {
            questions,
            title,
            description,
            image,
            isLoading: false,
        };
    } catch {
        return null;
    }
}

export const useSuggestedQuestions = (circleId: string | null): UseSuggestedQuestionsResult => {
    const api = useApi();

    const { data, isLoading } = useSWR(
        circleId ? `/circles/${circleId}/widget-post` : null,
        async () => {
            const response = await api.get({
                path: `circles/${circleId}/widget-post`,
            });
            return response?.post || null;
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            dedupingInterval: 60000,
        }
    );

    if (isLoading) {
        return { ...EMPTY_RESULT, isLoading: true };
    }

    // Try parsing from content.nsHtml (primary - admin writes in post editor)
    const nsHtml = data?.content?.nsHtml;
    if (nsHtml) {
        const parsed = parseWidgetContent(nsHtml, data?.title, data?.image);
        if (parsed) return parsed;
    }

    // Fallback: try parsing from metadata field (JSON format)
    if (data?.metadata) {
        try {
            const widget = typeof data.metadata === 'string'
                ? JSON.parse(data.metadata)
                : data.metadata;

            if (Array.isArray(widget.questions) && widget.questions.length > 0) {
                // Accept plain strings (back-compat) or { question|text, date?, from?, to? } objects.
                const questions: SuggestedQuestion[] = widget.questions
                    .map((q: unknown): SuggestedQuestion | null => {
                        if (typeof q === 'string') {
                            return q.trim() ? { text: q.trim() } : null;
                        }
                        if (!q || typeof q !== 'object') return null;
                        const obj = q as Record<string, unknown>;
                        const text = obj.text ?? obj.question;
                        if (typeof text !== 'string' || !text.trim()) return null;
                        const dateSpec: QuestionDateSpec = {};
                        if (typeof obj.date === 'string') dateSpec.token = obj.date;
                        if (typeof obj.from === 'string') dateSpec.from = obj.from;
                        if (typeof obj.to === 'string') dateSpec.to = obj.to;
                        return {
                            text: text.trim(),
                            dateSpec: dateSpec.token || dateSpec.from || dateSpec.to ? dateSpec : undefined,
                        };
                    })
                    .filter((q): q is SuggestedQuestion => q !== null);

                if (questions.length > 0) {
                    return {
                        questions,
                        title: widget.title,
                        description: widget.description,
                        image: widget.image,
                        isLoading: false,
                    };
                }
            }
        } catch {
            // Invalid JSON in metadata, ignore
        }
    }

    return EMPTY_RESULT;
};
